import { jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import React from "react";
import IntervalTree from "node-interval-tree";
import { sortBy, noop } from "lodash-es";
import { getRangeLength } from "@teselagen/range-utils";
import getRangeAngles from "./getRangeAnglesSpecial";
import getYOffset from "./getYOffset";
import withHover from "../helperComponents/withHover";
import PositionAnnotationOnCircle from "./PositionAnnotationOnCircle";
import getAnnotationNameAndStartStopString from "../utils/getAnnotationNameAndStartStopString";
import Feature from "./Feature";
import getAnnotationClassnames from "../utils/getAnnotationClassnames";
function drawAnnotations(props) {
  const {
    readOnly,
    annotationType,
    radius,
    noHover,
    isProtein,
    type,
    annotations,
    annotationHeight,
    noTitle,
    spaceBetweenAnnotations,
    sequenceLength,
    showCicularViewInternalLabels,
    // reverseAnnotations, //set true when drawing annotations that use the drawDirectedPiePiece function because that function returns things that need to be flipped
    // editorName,
    getColor,
    useStartAngle,
    //use the startAngle instead of the centerAngle to position the labels
    onClick = noop,
    noHeight,
    positionBy,
    //by default the annotation.start and annotation.end are used to position the annotation on the circle, but passing a function here gives an option to override that
    allOnSameLevel,
    //by default overlapping annotations are given different yOffsets. Setting this to true prevents that and positions all annotations on the same level (no y-offsets given). Cutsites for example just get drawn all on the same level
    onRightClicked = noop,
    onDoubleClick = noop,
    showLabels,
    hideAnnotation,
    // rotationRadians,
    labelOptions,
    annotationProps,
    fontStyle,
    labelColor
    // isZoomedIn,
    // visibleAngleRange
  } = props;
  const totalAnnotationHeight = annotationHeight + spaceBetweenAnnotations;
  const featureITree = new IntervalTree();
  let maxYOffset = 0;
  const svgGroup = [];
  const labels = {};
  if (!Object.keys(annotations).length) return null;
  sortBy(annotations, (a) => {
    return -getRangeLength(a, sequenceLength);
  }).map((annotation) => {
    const { startAngle, endAngle, totalAngle, centerAngle, locationAngles } = getRangeAngles(
      positionBy ? positionBy(annotation) : annotation,
      sequenceLength
    );
    const spansOrigin = startAngle > endAngle;
    const annotationCopy = {
      ...annotation,
      startAngle,
      endAngle,
      locationAngles,
      totalAngle,
      centerAngle,
      yOffset: 0
    };
    if (!allOnSameLevel) {
      const expandedEndAngle = spansOrigin ? endAngle + 2 * Math.PI : endAngle;
      let yOffset1;
      let yOffset2;
      if (spansOrigin) {
        annotationCopy.yOffset = getYOffset(
          featureITree,
          startAngle,
          expandedEndAngle
        );
      } else {
        yOffset1 = getYOffset(featureITree, startAngle, expandedEndAngle);
        yOffset2 = getYOffset(
          featureITree,
          startAngle + Math.PI * 2,
          expandedEndAngle + Math.PI * 2
        );
        annotationCopy.yOffset = Math.max(yOffset1, yOffset2);
      }
      if (spansOrigin) {
        featureITree.insert(startAngle, expandedEndAngle, {
          ...annotationCopy
        });
      } else {
        featureITree.insert(startAngle, expandedEndAngle, {
          ...annotationCopy
        });
        featureITree.insert(
          startAngle + 2 * Math.PI,
          expandedEndAngle + 2 * Math.PI,
          { ...annotationCopy }
        );
      }
      if (annotationCopy.yOffset > maxYOffset) {
        maxYOffset = annotationCopy.yOffset;
      }
    }
    return annotationCopy;
  }).forEach(function(annotation, index) {
    annotation.yOffset = maxYOffset - annotation.yOffset;
    function _onClick(event) {
      onClick && onClick({ event, annotation });
      annotation.onClick && annotation.onClick({ event, annotation });
    }
    function onContextMenu(event) {
      onRightClicked({ event, annotation });
      if (annotation.onRightClick) {
        annotation.onRightClick({ event, annotation });
      }
    }
    function _onDoubleClick(event) {
      onDoubleClick && onDoubleClick({ event, annotation });
      if (annotation.onDoubleClick) {
        annotation.onDoubleClick({ event, annotation });
      }
    }
    const {
      startAngle,
      endAngle,
      totalAngle,
      centerAngle,
      locationAngles,
      ...rest
    } = annotation;
    const _annotationProps = {
      ...annotationProps,
      ...rest
    };
    const titleText = getAnnotationNameAndStartStopString(annotation, {
      isProtein,
      readOnly
    });
    const classNames = getAnnotationClassnames(annotation, {
      viewName: "CircularView",
      type,
      isProtein,
      readOnly
    });
    const annotationRadius = radius + annotation.yOffset * totalAnnotationHeight;
    const name = annotation.name || annotation.restrictionEnzyme && annotation.restrictionEnzyme.name;
    let ellipsizedName;
    let angleAdjust;
    if (name && showCicularViewInternalLabels) {
      let getEllipsizedName = function(totalAngle2) {
        let ellipsizedName2;
        const arcLength = 2 * Math.PI * (annotationRadius - annotationHeight) * totalAngle2;
        const annLength = Math.max(0, Math.floor(arcLength / 55 - 3));
        ellipsizedName2 = name.slice(0, annLength);
        if (ellipsizedName2 && ellipsizedName2 !== name) {
          if (ellipsizedName2.length >= name.length - 2) {
            ellipsizedName2 = name;
          } else if (ellipsizedName2.length > 3) {
            ellipsizedName2 += "..";
          } else {
            ellipsizedName2 = void 0;
          }
        }
        return ellipsizedName2;
      };
      if (locationAngles) {
        annotation.locationAngles = locationAngles.map((l) => {
          const en = getEllipsizedName(l.totalAngle);
          if (en?.length > (ellipsizedName?.length || 0)) {
            ellipsizedName = en;
          }
          return {
            ...l,
            ellipsizedName: en
          };
        });
      } else {
        ellipsizedName = getEllipsizedName(totalAngle);
      }
    }
    const isRestrictionSite = annotation.type === "restriction_site";
    const isCutsite = annotationType === "cutsite" || annotationType === "restrictionSite";
    if (showLabels && !ellipsizedName) {
      labels[annotation.id] = {
        annotation,
        annotationType,
        annotationCenterAngle: useStartAngle ? startAngle : centerAngle,
        annotationCenterRadius: annotationRadius,
        text: name,
        id: annotation.id,
        title: titleText,
        className: `${classNames} ${annotation.labelClassName || ""}`,
        highPriorityLabel: annotation.highPriorityLabel,
        onClick: _onClick,
        onDoubleClick: _onDoubleClick,
        fontStyle: fontStyle || annotation.fontStyle || (isRestrictionSite || isCutsite || annotation.isImportant ? "italic" : "normal"),
        fontWeight: annotation.fontWeight || (isRestrictionSite || annotation.isImportant ? "bold" : "normal"),
        color: labelColor || (annotation.isImportant ? "black" : annotation.labelColor || (annotationType === "part" ? "#ac68cc" : "black")),
        onContextMenu,
        ...labelOptions
      };
    }
    if (!hideAnnotation) {
      const annotationColor = getColor ? getColor(annotation) : annotation.color || "#ac68cc";
      DrawAnnotation.displayName = annotationType + "--- DrawAnnotation";
      const CompToUse = noHover ? DrawAnnotationInner : DrawAnnotation;
      svgGroup.push(
        /* @__PURE__ */ createElement(
          CompToUse,
          {
            ...{
              ...props,
              ...rest,
              ...annotation,
              angleAdjust,
              ellipsizedName,
              name,
              annotationHeight,
              annotationRadius,
              annotationType,
              isProtein,
              noTitle,
              titleText,
              classNames,
              onClick: _onClick,
              onDoubleClick: _onDoubleClick,
              onContextMenu,
              annotation,
              annotationColor,
              totalAngle,
              centerAngle,
              annotationProps: _annotationProps
            },
            id: annotation.id,
            key: "veAnnotation-" + annotationType + index
          }
        )
      );
    }
  });
  return {
    component: /* @__PURE__ */ jsx(
      "g",
      {
        className: "veAnnotations-" + annotationType,
        children: svgGroup
      },
      "veAnnotations-" + annotationType
    ),
    height: noHeight ? 0 : maxYOffset * totalAnnotationHeight + 0.5 * annotationHeight,
    labels
  };
}
export default drawAnnotations;
function DrawAnnotationInner({
  className,
  startAngle,
  endAngle,
  onClick,
  onDoubleClick,
  onContextMenu,
  titleText,
  classNames,
  locationAngles,
  annotation,
  reverseAnnotations,
  Annotation = Feature,
  totalAngle,
  annotationColor,
  isProtein,
  annotationRadius,
  annotationHeight,
  onMouseLeave,
  onMouseOver,
  annotationType,
  annotationProps,
  addHeight,
  noTitle,
  useCenter,
  centerAngle,
  perAnnotationProps,
  passAnnotation,
  ellipsizedName,
  angleAdjust,
  rotationRadians
}) {
  const sharedProps = {
    style: noTitle ? void 0 : { cursor: "pointer" },
    className: `${className} ${classNames}`,
    onContextMenu,
    onClick,
    onDoubleClick,
    onMouseLeave,
    onMouseOver
  };
  function getInner({
    startAngle: startAngle2,
    endAngle: endAngle2,
    totalAngle: totalAngle2,
    isNotLocation,
    containsLocations,
    ellipsizedName: ellipsizedNameLocation
  }, i) {
    const { transform, revTransform } = PositionAnnotationOnCircle({
      sAngle: useCenter ? centerAngle : startAngle2,
      eAngle: useCenter ? centerAngle : endAngle2,
      height: addHeight ? annotationRadius : void 0,
      forward: reverseAnnotations ? !annotation.forward : annotation.forward
    });
    return /* @__PURE__ */ createElement(
      "g",
      {
        transform,
        "data-title": noTitle ? null : titleText,
        ...avoidOverlapWith,
        key: isNotLocation ? "notLocation" : "location--" + annotation.id + "--" + i,
        ...sharedProps
      },
      noTitle ? null : /* @__PURE__ */ jsx("g", { hidden: true, style: { display: "none" }, children: titleText }),
      /* @__PURE__ */ jsx(
        Annotation,
        {
          ...passAnnotation && { annotation },
          annotationType,
          arrowheadType: annotation.arrowheadType,
          totalAngle: totalAngle2,
          centerAngle,
          revTransform,
          rotationRadians,
          ellipsizedName: containsLocations ? ellipsizedNameLocation : ellipsizedName,
          locationNumber: i,
          angleAdjust,
          color: annotationColor,
          isProtein,
          containsLocations,
          radius: annotationRadius,
          annotationHeight,
          ...annotationProps,
          ...perAnnotationProps && perAnnotationProps(annotation)
        }
      )
    );
  }
  return /* @__PURE__ */ jsxs(React.Fragment, { children: [
    getInner({
      startAngle,
      endAngle,
      totalAngle,
      centerAngle,
      containsLocations: !!locationAngles
    }),
    locationAngles && locationAngles.map(getInner)
  ] });
}
const DrawAnnotation = withHover(DrawAnnotationInner);
export const avoidOverlapWith = {
  "data-avoid": ".topLevelLabelGroup",
  "data-avoid-backup": ".veLabel.veAnnotationHovered"
};
